import React, { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Text,
    Flex,
    Spinner,
    useToast,
    Badge,
    VStack,
    HStack,
    Card,
    CardBody,
    Progress,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Divider,
    Tooltip
} from '@chakra-ui/react';
import {
    CheckCircleIcon,
    WarningIcon,
    SearchIcon,
    InfoIcon
} from '@chakra-ui/icons';
import Sidebar from '../../../components/Dashboard/Sidebar';

import { fetchComprehensiveStats } from '../../../api/reportService';

// Corrections Modal Component
const CorrectionsModal = ({ isOpen, onClose, surah, corrections }) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Corrections for {surah?.name || `Surah ${surah?.surah_id}`}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {corrections && corrections.length > 0 ? (
                        <VStack spacing={6} align="stretch">
                            {corrections.map((correction, idx) => (
                                <Card key={idx} borderLeft="4px" borderColor="yellow.400">
                                    <CardBody>
                                        <VStack align="stretch" spacing={3}>
                                            <HStack justify="space-between">
                                                <Text fontWeight="bold" color="gray.700">
                                                    Ayah {correction.ayah_index}
                                                </Text>
                                                <Badge colorScheme="yellow">
                                                    {correction.corrections_count} {correction.corrections_count === 1 ? 'Correction' : 'Corrections'}
                                                </Badge>
                                            </HStack>

                                            <Text fontSize="sm" color="gray.600">
                                                Ayah Key: {correction.ayah_key}
                                            </Text>

                                            <Divider />

                                            <VStack align="stretch" spacing={4}>
                                                {correction.corrections.map((corr, corrIdx) => (
                                                    <Box
                                                        key={corrIdx}
                                                        p={3}
                                                        bg="gray.50"
                                                        borderRadius="md"
                                                    >
                                                        <VStack align="stretch" spacing={2}>
                                                            <HStack justify="space-between">
                                                                <Text fontSize="xs" color="gray.500">
                                                                    Expert #{corrIdx + 1}
                                                                </Text>
                                                                <Text fontSize="xs" color="gray.500">
                                                                    {new Date(corr.verified_at).toLocaleString()}
                                                                </Text>
                                                            </HStack>

                                                            {corr.corrections && (
                                                                <Box>
                                                                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                                                                        Correction:
                                                                    </Text>
                                                                    <Text
                                                                        fontSize="md"
                                                                        dir="rtl"
                                                                        textAlign="right"
                                                                        fontFamily="QuranFont"
                                                                        p={2}
                                                                        bg="white"
                                                                        borderRadius="md"
                                                                    >
                                                                        {corr.corrections}
                                                                    </Text>
                                                                </Box>
                                                            )}

                                                            {corr.comments && (
                                                                <Box>
                                                                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                                                                        Comments:
                                                                    </Text>
                                                                    <Text fontSize="sm" color="gray.700">
                                                                        {corr.comments}
                                                                    </Text>
                                                                </Box>
                                                            )}
                                                        </VStack>
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    ) : (
                        <Text textAlign="center" color="gray.500" py={8}>
                            No corrections found for this Surah
                        </Text>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button colorScheme="blue" onClick={onClose}>
                        Close
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// Stats Card Component
const StatsCard = ({ label, value, subValue, icon, colorScheme = "blue" }) => (
    <Card>
        <CardBody>
            <Stat>
                <Flex justify="space-between" align="center">
                    <Box>
                        <StatLabel color="gray.600">{label}</StatLabel>
                        <StatNumber fontSize="3xl" color={`${colorScheme}.600`}>
                            {value}
                        </StatNumber>
                        {subValue && (
                            <StatHelpText mb={0}>{subValue}</StatHelpText>
                        )}
                    </Box>
                    <Box
                        p={3}
                        bg={`${colorScheme}.100`}
                        borderRadius="full"
                        color={`${colorScheme}.600`}
                    >
                        {icon}
                    </Box>
                </Flex>
            </Stat>
        </CardBody>
    </Card>
);

// Surah Row Component
const SurahRow = ({ surah, onViewCorrections }) => {
    const totalAyahs = surah.ayas || (surah.verified_ayahs + surah.pending_verification + surah.pending_approval);
    const verificationPercentage = totalAyahs > 0
        ? ((surah.verified_ayahs + surah.pending_approval) / totalAyahs) * 100
        : 0;
    const approvalPercentage = totalAyahs > 0
        ? (surah.verified_ayahs / totalAyahs) * 100
        : 0;

    const getStatusBadge = (percentage) => {
        if (percentage >= 100) return { color: "green", text: "Complete" };
        if (percentage >= 75) return { color: "blue", text: "Near Complete" };
        if (percentage >= 50) return { color: "yellow", text: "In Progress" };
        if (percentage > 0) return { color: "orange", text: "Started" };
        return { color: "gray", text: "Pending" };
    };

    const verificationStatus = getStatusBadge(verificationPercentage);
    const approvalStatus = getStatusBadge(approvalPercentage);
    const hasCorrections = surah.verified_with_corrections && surah.verified_with_corrections.length > 0;

    return (
        <Tr _hover={{ bg: "gray.50" }}>
            <Td fontWeight="medium">{surah._id}</Td>
            <Td>
                <VStack align="start" spacing={0}>
                    <Text fontWeight="medium">{surah.name}</Text>
                    <Text fontSize="xs" color="gray.500">{surah.tname} - {surah.ename}</Text>
                </VStack>
            </Td>
            <Td>
                <Badge colorScheme={surah.type === 'Meccan' ? 'purple' : 'teal'} fontSize="xs">
                    {surah.type}
                </Badge>
            </Td>
            <Td isNumeric>{totalAyahs}</Td>
            <Td isNumeric>
                <Text color="green.600" fontWeight="semibold">
                    {surah.verified_ayahs}
                </Text>
            </Td>
            <Td isNumeric>
                <Text color="orange.600" fontWeight="semibold">
                    {surah.pending_verification}
                </Text>
            </Td>
            <Td isNumeric>
                <Text color="blue.600" fontWeight="semibold">
                    {surah.pending_approval}
                </Text>
            </Td>
            <Td>
                <VStack align="stretch" spacing={1}>
                    <HStack>
                        <Badge colorScheme={verificationStatus.color} fontSize="xs">
                            {verificationStatus.text}
                        </Badge>
                        <Text fontSize="xs" color="gray.600">
                            {verificationPercentage.toFixed(1)}%
                        </Text>
                    </HStack>
                    <Progress
                        value={verificationPercentage}
                        size="xs"
                        colorScheme={verificationStatus.color}
                        borderRadius="full"
                    />
                </VStack>
            </Td>
            <Td>
                <VStack align="stretch" spacing={1}>
                    <HStack>
                        <Badge colorScheme={approvalStatus.color} fontSize="xs">
                            {approvalStatus.text}
                        </Badge>
                        <Text fontSize="xs" color="gray.600">
                            {approvalPercentage.toFixed(1)}%
                        </Text>
                    </HStack>
                    <Progress
                        value={approvalPercentage}
                        size="xs"
                        colorScheme={approvalStatus.color}
                        borderRadius="full"
                    />
                </VStack>
            </Td>
            <Td textAlign="center">
                {hasCorrections ? (
                    <Tooltip label="View corrections">
                        <Button
                            size="sm"
                            colorScheme="yellow"
                            variant="outline"
                            leftIcon={<WarningIcon />}
                            onClick={() => onViewCorrections(surah)}
                        >
                            {surah.verified_with_corrections.length}
                        </Button>
                    </Tooltip>
                ) : (
                    <Text color="gray.400" fontSize="sm">-</Text>
                )}
            </Td>
        </Tr>
    );
};

// Main Report Component
const VerificationReportPage = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredSurahs, setFilteredSurahs] = useState([]);
    const [selectedSurah, setSelectedSurah] = useState(null);


    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    
    const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

    useEffect(() => {
        loadReport();
    }, []);

    useEffect(() => {
        if (reportData?.summary) {
            const filtered = reportData.summary.filter(surah => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    surah.name?.toLowerCase().includes(searchLower) ||
                    surah.tname?.toLowerCase().includes(searchLower) ||
                    surah.ename?.toLowerCase().includes(searchLower) ||
                    surah._id.toString().includes(searchTerm)
                );
            });
            setFilteredSurahs(filtered);
        }
    }, [searchTerm, reportData]);

    const loadReport = async () => {
        setLoading(true);
        try {
            const data = await fetchComprehensiveStats();
            setReportData(data);
            setFilteredSurahs(data.summary || []);
        } catch (err) {
            toast({
                title: 'Failed to load report',
                description: err.message || 'An error occurred while fetching the report',
                status: 'error',
                duration: 4000,
                isClosable: true
            });
            // Set empty data on error
            setReportData({ summary: [] });
            setFilteredSurahs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleViewCorrections = (surah) => {
        setSelectedSurah({
            surah_id: surah._id,
            name: surah.name,
            corrections: surah.verified_with_corrections
        });
        onOpen();
    };

    // Calculate overall statistics
    const calculateOverallStats = () => {
        if (!reportData?.summary || reportData.summary.length === 0) return null;

        const totalVerified = reportData.summary.reduce((sum, s) => sum + (s.verified_ayahs || 0), 0);
        const totalPendingVerification = reportData.summary.reduce((sum, s) => sum + (s.pending_verification || 0), 0);
        const totalPendingApproval = reportData.summary.reduce((sum, s) => sum + (s.pending_approval || 0), 0);
        const totalAyahs = reportData.summary.reduce((sum, s) => sum + (s.ayas || 0), 0);
        const totalCorrections = reportData.summary.reduce((sum, s) =>
            sum + (s.verified_with_corrections?.length || 0), 0
        );

        return {
            totalAyahs,
            totalVerified,
            totalPendingVerification,
            totalPendingApproval,
            totalCorrections,
            verificationPercentage: totalAyahs > 0 ? ((totalVerified + totalPendingApproval) / totalAyahs) * 100 : 0,
            approvalPercentage: totalAyahs > 0 ? (totalVerified / totalAyahs) * 100 : 0
        };
    };

    if (loading) {
        return (
            <Flex h="100vh" justify="center" align="center">
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                    <Text color="gray.600">Loading verification report...</Text>
                </VStack>
            </Flex>
        );
    }

    const stats = calculateOverallStats();

    return (

        <Flex direction="row" h="100vh">
            <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} />
            <Box
                pl={sidebarCollapsed ? "60px" : "250px"}
                w="full"
                overflowY="auto"
                pb={8}
                transition="padding-left 0.2s ease"
            >
                <Box minH="100vh" bg="gray.50" p={8}>
                    <VStack spacing={6} align="stretch">
                        {/* Header */}
                        <Box>
                            <Heading size="xl" mb={2}>
                                Verification Progress Report
                            </Heading>
                            <Text color="gray.600">
                                Track expert verification and super expert approval progress across all Surahs
                            </Text>
                        </Box>

                        {/* Overall Statistics */}
                        {stats && (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                                <StatsCard
                                    label="Total Ayahs"
                                    value={stats.totalAyahs.toLocaleString()}
                                    subValue="Across all Surahs"
                                    icon={<InfoIcon boxSize={6} />}
                                    colorScheme="blue"
                                />
                                <StatsCard
                                    label="Fully Approved"
                                    value={stats.totalVerified.toLocaleString()}
                                    subValue={`${stats.approvalPercentage.toFixed(1)}% Complete`}
                                    icon={<CheckCircleIcon boxSize={6} />}
                                    colorScheme="green"
                                />
                                <StatsCard
                                    label="Pending Approval"
                                    value={stats.totalPendingApproval.toLocaleString()}
                                    subValue="Expert verified"
                                    icon={<CheckCircleIcon boxSize={6} />}
                                    colorScheme="blue"
                                />
                                <StatsCard
                                    label="Pending Verification"
                                    value={stats.totalPendingVerification.toLocaleString()}
                                    subValue={`${stats.totalCorrections} with corrections`}
                                    icon={<WarningIcon boxSize={6} />}
                                    colorScheme="orange"
                                />
                            </SimpleGrid>
                        )}

                        {/* Overall Progress Visualization */}
                        {stats && (
                            <Card>
                                <CardBody>
                                    <Heading size="md" mb={4}>Overall Progress</Heading>
                                    <VStack spacing={4} align="stretch">
                                        <Box>
                                            <Flex justify="space-between" mb={2}>
                                                <Text fontSize="sm" fontWeight="medium">Expert Verification Progress</Text>
                                                <Text fontSize="sm" color="gray.600">
                                                    {stats.totalVerified + stats.totalPendingApproval} / {stats.totalAyahs} ({stats.verificationPercentage.toFixed(1)}%)
                                                </Text>
                                            </Flex>
                                            <Progress
                                                value={stats.verificationPercentage}
                                                colorScheme="green"
                                                borderRadius="full"
                                                size="md"
                                            />
                                        </Box>
                                        <Box>
                                            <Flex justify="space-between" mb={2}>
                                                <Text fontSize="sm" fontWeight="medium">Super Expert Approval Progress</Text>
                                                <Text fontSize="sm" color="gray.600">
                                                    {stats.totalVerified} / {stats.totalAyahs} ({stats.approvalPercentage.toFixed(1)}%)
                                                </Text>
                                            </Flex>
                                            <Progress
                                                value={stats.approvalPercentage}
                                                colorScheme="purple"
                                                borderRadius="full"
                                                size="md"
                                            />
                                        </Box>
                                    </VStack>
                                </CardBody>
                            </Card>
                        )}

                        {/* Surah-wise Progress Table */}
                        <Card>
                            <CardBody>
                                <Flex justify="space-between" align="center" mb={4}>
                                    <Heading size="md">Surah-wise Progress</Heading>
                                    <InputGroup maxW="300px">
                                        <InputLeftElement pointerEvents="none">
                                            <SearchIcon color="gray.400" />
                                        </InputLeftElement>
                                        <Input
                                            placeholder="Search surah..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Flex>

                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>#</Th>
                                                <Th>Surah Name</Th>
                                                <Th>Type</Th>
                                                <Th isNumeric>Total</Th>
                                                <Th isNumeric>Approved</Th>
                                                <Th isNumeric>Pending Verification</Th>
                                                <Th isNumeric>Pending Approval</Th>
                                                <Th>Expert Verification</Th>
                                                <Th>Super Approval</Th>
                                                <Th textAlign="center">Corrections</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredSurahs.map((surah) => (
                                                <SurahRow
                                                    key={surah._id}
                                                    surah={surah}
                                                    onViewCorrections={handleViewCorrections}
                                                />
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>

                                {filteredSurahs.length === 0 && (
                                    <Flex justify="center" py={8}>
                                        <Text color="gray.500">
                                            {searchTerm ? 'No surahs found matching your search' : 'No data available'}
                                        </Text>
                                    </Flex>
                                )}
                            </CardBody>
                        </Card>
                    </VStack>

                    {/* Corrections Modal */}
                    <CorrectionsModal
                        isOpen={isOpen}
                        onClose={onClose}
                        surah={selectedSurah}
                        corrections={selectedSurah?.corrections || []}
                    />
                </Box>
            </Box>
        </Flex>
    );
};

export default VerificationReportPage;